import { inject } from '@angular/core';
import {
    signalStore,
    withState,
    withMethods,
    patchState,
} from '@ngrx/signals';
import { lastValueFrom } from 'rxjs';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Announcement } from '../../api-v3/models/announcement';
import { PublicService } from '../../api-v3/services/public.service';

export interface SeenAnnouncement {
    key: string;
    startingAt: number;
    endingAt: number | null;
}

export interface AnnouncementsState {
    announcements: Announcement[];
    allActiveAnnouncements: Announcement[];
    hasNewAnnouncements: boolean;
}

const initialState: AnnouncementsState = {
    announcements: [],
    allActiveAnnouncements: [],
    hasNewAnnouncements: false,
};

const SEEN_ANNOUNCEMENTS_KEY = 'seenAnnouncementKeys';

export const AnnouncementsStore = signalStore(
    { providedIn: 'root' },
    withState<AnnouncementsState>(initialState),
    withMethods(store => {
        const publicService = inject(PublicService);
        const storageService = inject(StorageService);
        const toastService = inject(ToastService);
        const translateService = inject(TranslateService);

        let announcementLoadFailed = false;

        const getSeenAnnouncements = (): SeenAnnouncement[] => {
            try {
                const stored =
                    storageService.getItemAsObject(SEEN_ANNOUNCEMENTS_KEY);
                if (!Array.isArray(stored)) {
                    return [];
                }
                if (stored.length > 0 && typeof stored[0] === 'string') {
                    const migrated: SeenAnnouncement[] = stored.map(
                        (key: string) => ({ key, startingAt: 0, endingAt: null })
                    );
                    storageService.setItem(SEEN_ANNOUNCEMENTS_KEY, migrated);
                    return migrated;
                }
                return stored as SeenAnnouncement[];
            } catch {
                return [];
            }
        };

        const loadAndFilterAnnouncements = async (): Promise<void> => {
            try {
                const announcements: Announcement[] = await lastValueFrom(
                    publicService.getGlobalAnnouncements()
                );

                const currentTimestamp = Date.now();

                const seenAnnouncements = getSeenAnnouncements();

                const allActiveAnnouncements = announcements
                    .filter(announcement => {
                        const hasStarted =
                            announcement.startingAt <= currentTimestamp;
                        const hasNotEnded =
                            announcement.endingAt === null ||
                            announcement.endingAt === undefined ||
                            announcement.endingAt > currentTimestamp;

                        return hasStarted && hasNotEnded;
                    })
                    .sort((a, b) => b.startingAt - a.startingAt);

                patchState(store, { allActiveAnnouncements });

                patchState(store, {
                    announcements: allActiveAnnouncements.filter(announcement => {
                        const seenEntry = seenAnnouncements.find(
                            s => s.key === announcement._key
                        );
                        if (!seenEntry) {
                            return true;
                        }
                        if (seenEntry.startingAt === 0) {
                            return false;
                        }
                        const endingAt = announcement.endingAt ?? null;
                        return (
                            seenEntry.startingAt !== announcement.startingAt ||
                            seenEntry.endingAt !== endingAt
                        );
                    }),
                });

                // Cleanup stale seen entries
                const activeKeys = new Set(
                    store.allActiveAnnouncements().map(a => a._key)
                );
                const cleanedSeen = seenAnnouncements.filter(s =>
                    activeKeys.has(s.key)
                );
                if (cleanedSeen.length !== seenAnnouncements.length) {
                    storageService.setItem(SEEN_ANNOUNCEMENTS_KEY, cleanedSeen);
                }

                patchState(store, {
                    hasNewAnnouncements: store.announcements().length > 0,
                });

                announcementLoadFailed = false;
            } catch (error) {
                if (!announcementLoadFailed) {
                    announcementLoadFailed = true;
                    toastService.error(
                        translateService.instant(
                            'ADMIN-PANEL.ANNOUNCEMENTS.GET-ERROR'
                        )
                    );
                }
            }
        };

        const markAnnouncementsAsSeen = async (): Promise<void> => {
            if (
                !store.announcements() ||
                store.announcements().length === 0
            ) {
                return;
            }

            const currentSeen = getSeenAnnouncements();
            const seenMap = new Map<string, SeenAnnouncement>();
            for (const entry of currentSeen) {
                seenMap.set(entry.key, entry);
            }
            for (const a of store.announcements()) {
                seenMap.set(a._key, {
                    key: a._key,
                    startingAt: a.startingAt,
                    endingAt: a.endingAt ?? null,
                });
            }

            storageService.setItem(
                SEEN_ANNOUNCEMENTS_KEY,
                Array.from(seenMap.values())
            );

            await loadAndFilterAnnouncements();
        };

        return {
            getSeenAnnouncements,
            loadAndFilterAnnouncements,
            markAnnouncementsAsSeen,
        };
    })
);
